/**
 * Pre-match device and connection check.
 *
 * Every item is a live reading, not a claim: latency comes from the ping loop,
 * the microphone bar moves when you speak, the gesture row lights up when you
 * actually draw. If speech is unavailable the player is offered the keyboard
 * fallback and the opponent is told about it.
 *
 * The practice range runs the same check, minus the rows that are about a
 * second human — there is a training dummy in the far stance, not a peer, so
 * "opponent joined" and the voice link have nothing to report.
 */
import { useEffect, useRef, useState } from 'react';
import { C2S } from '@spell/network-protocol';
import { net } from '../networking/net';
import { useNet, selectOpponent, selectSelf } from '../networking/netStore';
import { mic, chantRecognizer, micLevels, speechSupported } from '../voice/mic';
import { voiceBridge, voiceLink, prefetchIceServers } from '../networking/webrtc';
import { useCastUi } from '../gameplay/castState';
import { GesturePad } from '../crafting/GesturePad';
import { sfx } from '../audio/sfx';

export interface ConnectionCheckProps {
  /** Solo practice: no peer, no voice link, no invite code worth sharing. */
  practice?: boolean;
  /** Offered on the practice check, where backing out costs nobody anything. */
  onLeave?: () => void;
}

export function ConnectionCheck({ practice = false, onLeave }: ConnectionCheckProps) {
  const self = useNet(selectSelf);
  const peer = useNet(selectOpponent);
  // The training dummy sits in the opponent slot; it is not somebody to wait
  // for, hand a microphone to, or open a voice link with.
  const opponent = practice ? undefined : peer;
  const roomCode = useNet((s) => s.roomCode);
  const speedScale = useNet((s) => s.speedScale);
  const setFallback = useCastUi((s) => s.setFallback);
  const usingFallback = useCastUi((s) => s.usingFallbackInput);

  const [micGranted, setMicGranted] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [gestureOk, setGestureOk] = useState(false);
  const [ready, setReady] = useState(false);
  const levelRef = useRef<HTMLDivElement>(null);
  const latencyRef = useRef<HTMLSpanElement>(null);
  const remoteRef = useRef<HTMLSpanElement>(null);

  /* Ask for the microphone once, and start recognition when it is supported. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ok = await mic.start();
      if (cancelled) return;
      setMicGranted(ok);
      setMicError(mic.error);
      const supported = speechSupported();
      if (ok && supported) chantRecognizer.start();
      const fallback = !ok || !supported;
      setFallback(fallback);
      net.send(C2S.DEVICE_CHECK, {
        micGranted: ok,
        speechSupported: supported,
        usingFallbackInput: fallback,
        gestureOk: false,
        voiceLevel: 0,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [setFallback]);

  /**
   * Warm the ICE credentials while the player is still reading this screen.
   *
   * The relay list is fetched from the game server, and asking for it here
   * means the round trip is already paid for by the time both duellists are
   * known and the link below is opened.
   */
  useEffect(() => {
    if (practice) return;
    void prefetchIceServers();
  }, [practice]);

  /**
   * Start the voice link once both duellists are known.
   *
   * Which side offers must be decided identically on both clients and without
   * depending on state-sync timing, so it is derived from the two session ids:
   * the lower id offers, the higher one answers. Deciding it from room state
   * that may not have arrived yet left both peers "polite", and a glare then
   * left the link half-open — audio flowed one way only.
   */
  useEffect(() => {
    if (!opponent || !micGranted) return;
    const selfId = useNet.getState().sessionId;
    if (!selfId) return;
    // Deliberately no cleanup: this screen unmounts when the first round
    // starts, and the voice link has to outlive it. It is torn down when the
    // player leaves the room.
    void voiceBridge.start(selfId < opponent.sessionId);
  }, [opponent?.sessionId, micGranted]);

  /* If recognition fails after starting, switch to keyboard chants. */
  useEffect(() => {
    return chantRecognizer.onFailure(() => setFallback(true));
  }, [setFallback]);

  /* Live meters. */
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (levelRef.current) {
        levelRef.current.style.transform = `scaleX(${Math.min(1, micLevels.level * 8).toFixed(3)})`;
      }
      if (latencyRef.current) {
        latencyRef.current.textContent = `${Math.round(net.rtt)}ms`;
      }
      if (remoteRef.current) {
        remoteRef.current.textContent = voiceLink.connected
          ? `수신 중 ${Math.round(Math.min(1, voiceLink.remoteLevel * 8) * 100)}%`
          : '연결 대기';
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* Report the check result to the server whenever it changes. */
  useEffect(() => {
    net.send(C2S.DEVICE_CHECK, {
      micGranted,
      speechSupported: speechSupported(),
      usingFallbackInput: usingFallback,
      gestureOk,
      voiceLevel: Math.min(1, micLevels.level * 8),
    });
  }, [micGranted, usingFallback, gestureOk]);

  const confirm = () => {
    setReady(true);
    sfx.ui();
    net.send(C2S.READY);
  };

  const waiting = !opponent;

  return (
    <div className="screen">
      <div className="panel lobby" data-testid={practice ? 'practice-check' : 'connection-check'}>
        <h2 className="title" style={{ fontSize: 26 }}>{practice ? '연습장 준비' : '연결 확인'}</h2>
        {practice ? (
          <p className="subtitle">
            훈련 인형을 상대로 시간 제한 없이 연습합니다. 채점, 피해량, 패링 판정 구간은 실제 대련과 같은 서버 규칙을
            그대로 사용합니다.
          </p>
        ) : (
          <p className="subtitle">
            초대 코드 <b style={{ letterSpacing: '0.24em' }}>{roomCode}</b>
            {waiting ? ' — 상대를 기다리는 중입니다.' : ` — ${opponent?.name} 님과 대련합니다.`}
          </p>
        )}

        <ul className="check-list">
          <li className={`check-item ${net.room ? 'check-item--ok' : 'check-item--bad'}`}>
            <span className="check-item__icon">{net.room ? '✓' : '✕'}</span>
            <span>서버 연결</span>
            <span className="check-item__detail">
              지연 <span ref={latencyRef}>—</span>
            </span>
          </li>
          {practice ? null : (
            <li className={`check-item ${waiting ? 'check-item--warn' : 'check-item--ok'}`}>
              <span className="check-item__icon">{waiting ? '…' : '✓'}</span>
              <span>상대 입장</span>
              <span className="check-item__detail">{opponent ? opponent.name : '대기 중'}</span>
            </li>
          )}
          <li className={`check-item ${micGranted ? 'check-item--ok' : 'check-item--warn'}`}>
            <span className="check-item__icon">{micGranted ? '🎙' : '⌨'}</span>
            <span>{micGranted ? '마이크 입력' : '마이크 없음 — 키보드 영창'}</span>
            <div className="level-meter">
              <div className="level-meter__fill" ref={levelRef} />
            </div>
          </li>
          <li className={`check-item ${speechSupported() ? 'check-item--ok' : 'check-item--warn'}`}>
            <span className="check-item__icon">{speechSupported() ? '✓' : '!'}</span>
            <span>{speechSupported() ? '영어 영창 인식 지원' : '이 브라우저는 음성 인식을 지원하지 않습니다'}</span>
            <span className="check-item__detail">{speechSupported() ? 'Web Speech API' : '대체 입력 사용'}</span>
          </li>
          {practice ? null : (
            <li className={`check-item ${voiceLink.connected ? 'check-item--ok' : 'check-item--warn'}`}>
              <span className="check-item__icon">🔊</span>
              <span>상대 음성 수신</span>
              <span className="check-item__detail" ref={remoteRef}>
                연결 대기
              </span>
            </li>
          )}
          {/* A room rule, not a preference: it changes how long a shot's flight
              is to read, so both duellists see it before either readies. */}
          <li className="check-item check-item--ok">
            <span className="check-item__icon">⤳</span>
            <span>
              이 방의 탄속{' '}
              {speedScale < 1 ? '— 느림 (읽을 시간이 깁니다)' : speedScale > 1 ? '— 빠름' : '— 기본'}
            </span>
            <span className="check-item__detail" data-testid="room-speed">
              {speedScale.toFixed(2)}×
            </span>
          </li>
          <li className={`check-item ${gestureOk ? 'check-item--ok' : 'check-item--warn'}`}>
            <span className="check-item__icon">{gestureOk ? '✓' : '✎'}</span>
            <span>트랙패드 / 마우스 제스처</span>
            <span className="check-item__detail">{gestureOk ? '입력 확인됨' : '아래에 한 번 그려보세요'}</span>
          </li>
        </ul>

        {micError ? <div className="error">{micError}</div> : null}

        <div className="section-label">제스처 테스트</div>
        <div style={{ maxWidth: 260 }}>
          <GesturePad
            label="아무 모양이나 그려보세요"
            onChange={(points) => {
              if (points > 8 && !gestureOk) setGestureOk(true);
            }}
          />
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <label className="row" style={{ gap: 6 }}>
            <input
              type="checkbox"
              data-testid="toggle-fallback"
              checked={usingFallback}
              onChange={(e) => setFallback(e.target.checked)}
            />
            <span className="hint">키보드로 영창 입력 (음성 대신)</span>
          </label>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn--primary"
            data-testid="ready-button"
            disabled={ready}
            onClick={confirm}
          >
            {practice ? '연습 시작' : ready ? '상대를 기다리는 중…' : '준비 완료'}
          </button>
          {practice ? (
            <button type="button" className="btn btn--ghost" data-testid="practice-check-leave" onClick={onLeave}>
              로비로
            </button>
          ) : (
            <span className="hint">
              {self?.ready && opponent?.ready
                ? '두 명 모두 준비되었습니다.'
                : '두 명이 모두 준비하면 1라운드가 시작됩니다.'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
